import React from "react";
import { Box, Button, Card, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import IntegrationInstructionsOutlinedIcon from "@mui/icons-material/IntegrationInstructionsOutlined";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { IconTile } from "../../components/ui";
import { CheckList, DirArrow, FeatureCard, PageHero, Section } from "./MarketingParts";

export default function MarketingInstallGuide() {
  const { t } = useTranslation();

  const steps = [t("m_install_steps_1"), t("m_install_steps_2"), t("m_install_steps_3"), t("m_install_steps_4")];

  return (
    <>
      <PageHero
        eyebrow={t("mk_install_page_eyebrow")}
        icon={<IntegrationInstructionsOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />}
        title={t("m_install_title")}
        subtitle={t("mk_install_page_sub")}
        actions={
          <>
            <Button variant="contained" size="large" component={RouterLink} to="/register" endIcon={<DirArrow />}>
              {t("mk_install_start")}
            </Button>
            <Button variant="outlined" size="large" component={RouterLink} to="/pricing">
              {t("mk_install_pricing")}
            </Button>
          </>
        }
      />

      <Section sx={{ pt: { xs: 5, md: 7 } }}>
        <Typography variant="h2" sx={{ fontSize: { xs: "1.4rem", md: "1.6rem" }, mb: 3 }}>
          {t("mk_install_page_steps_t")}
        </Typography>
        <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
          {steps.map((label, i) => (
            <Box component="li" key={label}>
              <Card sx={{ p: 2.5, height: "100%" }}>
                <Box
                  aria-hidden="true"
                  sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: (th) => th.aw.accentSoft, color: "primary.main", display: "grid", placeItems: "center", fontWeight: 800, mb: 1.75 }}
                >
                  {i + 1}
                </Box>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.5 }}>{label}</Typography>
              </Card>
            </Box>
          ))}
        </Box>

        <Typography variant="h2" sx={{ fontSize: { xs: "1.4rem", md: "1.6rem" }, mt: { xs: 6, md: 8 }, mb: 3 }}>
          {t("mk_install_page_method_t")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 2.5, md: 3 } }}>
          <FeatureCard>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <IconTile size={44}>
                <CodeRoundedIcon />
              </IconTile>
              <Typography variant="h3" sx={{ fontSize: "1.15rem" }}>
                {t("mk_install_tab_code")}
              </Typography>
            </Stack>
            <CheckList dense items={[t("mk_install_point_1"), t("mk_install_code_note"), t("mk_install_point_3")]} />
          </FeatureCard>
          <FeatureCard>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <IconTile size={44} tone="violet">
                <ExtensionOutlinedIcon />
              </IconTile>
              <Typography variant="h3" sx={{ fontSize: "1.15rem" }}>
                {t("mk_install_tab_wp")}
              </Typography>
            </Stack>
            <CheckList dense items={[t("mk_install_wp_1"), t("mk_install_wp_2"), t("mk_install_wp_3"), t("mk_install_wp_note")]} />
          </FeatureCard>
        </Box>

        <Card
          sx={{
            mt: { xs: 2.5, md: 3 },
            p: { xs: 3, md: 3.5 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { sm: "center" },
            gap: 2.5,
            bgcolor: (th) => th.aw.accentSoft,
            boxShadow: "none",
          }}
        >
          <IconTile size={48} variant="solid">
            <ShieldOutlinedIcon />
          </IconTile>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h2" sx={{ fontSize: "1.15rem", mb: 0.5 }}>
              {t("mk_resilience_t")}
            </Typography>
            <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>{t("mk_resilience_d")}</Typography>
          </Box>
          <Button variant="outlined" component={RouterLink} to="/support" sx={{ flex: "none" }}>
            {t("mk_install_issues")}
          </Button>
        </Card>
      </Section>
    </>
  );
}
